import { useCallback, useEffect, useState } from "react";

import {
  FilterFieldOption,
  FilterFieldOptionsHookResult,
} from "~/features/filter";

interface IPermissionDescription {
  id: string;
  description: string;
  moduleId: string;
}

interface IPermissionModule {
  id: string;
  description: string;
  permissions: IPermissionDescription[];
}

/**
 * Опции фильтра по правам/доступам пользователя.
 * Берёт каталог из /internal-api/auth/access/descriptions (дерево модулей)
 * и разворачивает его в плоский список для мультиселекта.
 */
export const usePermissionOptions = (): FilterFieldOptionsHookResult => {
  const [options, setOptions] = useState<FilterFieldOption[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchPermissions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch("/internal-api/auth/access/descriptions", {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data: { modules: IPermissionModule[] } = await response.json();

      // Группы по модулям: название модуля — заголовок раздела,
      // далее его права; между модулями Select рисует разделитель.
      setOptions(
        (data.modules ?? []).map((module) => ({
          label: module.description,
          value: module.id,
          options: (module.permissions ?? []).map((perm) => ({
            value: String(perm.id),
            label: perm.description,
          })),
        })),
      );
    } catch (error) {
      console.error("Ошибка загрузки прав доступа:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  return { options, loading };
};
